import React, { useState, useEffect, useCallback, useRef  } from 'react';
import apiClient from '../../../services/apiClient';
import apiClientOutage from '../../../services/apiClientOutage';
import OutageForm from '../Outage-Form/OutageForm';
import OutageTable from '../Outage-Table/OutageTable';
// import ErrorDialog from '../ErrorDialog/ErrorDialog'; 
import Dialog from "../../Admin/FormDialog/Dialog";
import { getAssetHeaders, getOutageHeaders } from "../apiHeadersOutage";
import { datadogRum } from '@datadog/browser-rum';
import LoadingStyle from "../../Loading/Loading";
import { DateTime } from "luxon";
import { getUserInfo } from "../../../services/authService-sighup";
import { SmartPolling } from '../SmartPolling';
import { OutageAssetScope, parseOutageAssetScope } from '../outageAssetScope';



interface Outage {
  outage_id: number;
  reference_id: number;
  reference_type_code: string;
  start_datetime: string;
  end_datetime: string;
  outage_status_code: string;
  workflow_type_code: string;
  comments: Comment[];
  sys_created_by: string;
  sys_modified_by: string;
  sys_created_date: string;
  sys_modified_date: string;
}

const OutageSetup: React.FC = () => {
  const [assets, setAssets] = useState<any[]>([]);
  const [assetScope, setAssetScope] = useState<OutageAssetScope | null>(null);
  const [assetScopeError, setAssetScopeError] = useState<string | null>(null);
  const [outages, setOutages] = useState<any[]>([]);
  const outagesRef = useRef<Outage[]>([]);
  const [selectedOutage, setSelectedOutage] = useState<any | null>(null);
  const [error, setError] = useState<{ title: string; details: string } | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [selectedRow, setSelectedRow] = useState(null);
  const [isFormDirty, setIsFormDirty] = useState(false);
  const [isCreateMode, setIsCreateMode] = useState(false);
  const [user, setUser] = useState<{ fullName: string; email: string } | null>(null);
  
  const firstLoadRef = useRef(true);

  const isFetchingRef = useRef(false);


  const [isHistoricalOn, setIsHistoricalOn] = useState(false);

  const [tableWidthPct, setTableWidthPct] = useState(66.67);
  const isDraggingRef = useRef(false);
  const containerRef = useRef<HTMLDivElement>(null);

  const handleDividerMouseDown = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    isDraggingRef.current = true;

    const onMouseMove = (moveEvent: MouseEvent) => {
      if (!isDraggingRef.current || !containerRef.current) return;
      const rect = containerRef.current.getBoundingClientRect();
      const pct = ((moveEvent.clientX - rect.left) / rect.width) * 100;
      setTableWidthPct(Math.min(Math.max(pct, 25), 80));
    };

    const onMouseUp = () => {
      isDraggingRef.current = false;
      window.removeEventListener('mousemove', onMouseMove);
      window.removeEventListener('mouseup', onMouseUp);
    };

    window.addEventListener('mousemove', onMouseMove);
    window.addEventListener('mouseup', onMouseUp);
  }, [])




  const [dialog, setDialog] = useState<{
    open: boolean;
    title: string;
    message: React.ReactNode;
    confirmButtonText: string;
    cancelButtonText?: string;
    onConfirm: () => void;
    onCancel?: () => void;
  }>({
    open: false,
    title: "",
    message: "",
    confirmButtonText: "",
    onConfirm: () => {},
  });

  const closeDialog = () => {
    setDialog({
      open: false,
      title: "",
      message: "",
      confirmButtonText: "",
      onConfirm: () => {},
    });
  };

  // A 409 now means one of two different things: the long-standing duplicate-record
  // guard, or the ISO-ID uniqueness check in outage-data-service. Tell them apart from
  // the server's message, otherwise an ISO-ID clash reports itself as a Completed
  // duplicate. Returns true when it has shown a dialog.
  const handleConflictError = (err: any, fallbackTitle: string): boolean => {
    if (err?.response?.status !== 409) return false;

    const data = err?.response?.data;
    const serverMessage = String(
      data?.error?.error?.message
      ?? data?.error?.message
      ?? data?.detail
      ?? data?.message
      ?? ""
    );
    console.error(`[${fallbackTitle}] HTTP 409:`, serverMessage || data);

    const isIsoIdConflict = /ISO-ID/i.test(serverMessage);

    setDialog({
      open: true,
      title: isIsoIdConflict
        ? "Conflict: ISO-ID Already In Use"
        : "Conflict: Duplicate Record Detected",
      message: isIsoIdConflict ? (
        <span>
          {serverMessage.replace(/^Integrity violation:\s*/i, "")}
          <br /><br />
          A numeric ISO-ID can belong to only one outage that is not yet{" "}
          <strong>Completed</strong> or <strong>Deleted</strong>.
        </span>
      ) : (
        <span>
          This outage could not be saved because a record with the same configuration already exists in a{" "}
          <strong>Completed</strong> state.
          <br /><br />
          Please review the existing completed outages for this asset/resource and date range before proceeding.
        </span>
      ),
      confirmButtonText: "Close",
      onConfirm: closeDialog,
    });

    try { datadogRum.addError(err, { title: fallbackTitle, details: serverMessage }); } catch (_) {}
    return true;
  };

  const handleApiError = (err: any, fallbackTitle: string) => {
    const status = err?.response?.status;
    const data = err?.response?.data;

    const extractMessage = (d: any): string | undefined =>
      d?.error?.error?.message
      ?? d?.error?.message
      ?? d?.detail
      ?? d?.message
      ?? (typeof d === "string" && d.length ? d : undefined);

    const rawMsg = extractMessage(data);

    // Log full technical details to console for debugging
    console.error(`[${fallbackTitle}] HTTP ${status ?? "?"}:`, rawMsg ?? err?.message);

    // Show a user-friendly message in the popup
    const friendlyMessages: Record<number, string> = {
      400: "The request was invalid. Please check your inputs and try again.",
      401: "You are not authorized. Please sign in and try again.",
      403: "You don't have permission to perform this action.",
      404: "The requested resource was not found.",
      409: "This action couldn't be completed due to a conflict with existing data. Please verify your inputs and try again.",
      422: "The submitted data is invalid. Please review your entries.",
      500: "An unexpected server error occurred. Please try again or contact support.",
      503: "The service is temporarily unavailable. Please try again shortly.",
    };
    const details = (status && friendlyMessages[status]) ?? fallbackTitle;

    setError({ title: fallbackTitle, details });
    setDialog({
      open: true,
      title: fallbackTitle,
      message: details,
      confirmButtonText: "Close",
      onConfirm: closeDialog,
    });
    try { datadogRum.addError(err, { title: fallbackTitle, details: rawMsg ?? details }); } catch (_) {}
  };

  useEffect(() => {
    const fetchUserInfo = () => {
      const userInfo = getUserInfo();
      if (userInfo) {
        setUser({
          fullName: userInfo.fullName || "Unknown User",
          email: userInfo.email || "",
        });
      }
    };
    fetchUserInfo();
  }, []);


  const fetchAssets = async () => {
    setLoading(true);
    try {
      const headers = await getAssetHeaders();
  
      let allAssets: any[] = [];
      let page = 1;
      const pageSize = 300;
      let hasMoreData = true;
  
      while (hasMoreData) {
        try {
          const response = await apiClient.get(
            `/assets/?page=${page}&page_size=${pageSize}`,
            { headers }
          );
  
          if (response.data.response?.status === "success") {
            const newAssets = response.data.response.data;
  
            allAssets = [...allAssets, ...newAssets];
            hasMoreData = newAssets.length === pageSize;
            page++;
          } else {
            handleApiError(null, "API returned unsuccessful status while fetching assets.");
            hasMoreData = false;
          }
        } catch (error: any) {
          if (error.response?.status === 404) {
            console.log("No more assets.");
            hasMoreData = false;
          } else {
            handleApiError(error, "Failed to fetch paginated assets.");
            break;
          }
        }
      }
  
      // Fetch resources after all assets are fetched
      try {
        const resourceResponse = await apiClient.get(
          `/ref-data/resources`,
          { headers }
        );
  
        let resourcesData: any[] = [];
  
        if (resourceResponse.data.response?.status === "success") {
          resourcesData = resourceResponse.data.response.data;
        } else {
          handleApiError(null, "API returned unsuccessful status while fetching resources.");
        }
  
        // Combine assets with their resources
        const assetsWithResources = allAssets.map((asset) => {
          const assetResources = resourcesData
            .filter((resource) => resource.asset_id === asset.asset_id)
            .map(({ resource_id, iso_resource_name, iso_code }) => ({
              resource_id,
              iso_resource_name,
              iso_code,
            }));
  
          return { ...asset, resources: assetResources };
        });
  
        setAssets(assetsWithResources);
        // setError(null);
      } catch (error) {
        handleApiError(error, "Failed to fetch resource data.");
      }
    } catch (error) {
      handleApiError(error, "Error fetching assets.");
      // setError("Error fetching assets. Please try again.");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    fetchAssets();
  }, []);

  // Asset ids the caller's outage permission allows; drives the form dropdown only.
  // Any failure sets the error: never fall back to the unfiltered list.
  useEffect(() => {
    let cancelled = false;
    const fetchAssetScope = async () => {
      try {
        const response = await apiClientOutage.get('/outage-metadata/assets', {
          headers: await getOutageHeaders(),
        });
        if (response.data.response?.status !== "success") {
          throw new Error("Outage asset scope request was not successful");
        }
        const scope = parseOutageAssetScope(response.data.response.data);
        if (!cancelled) setAssetScope(scope);
      } catch (error) {
        console.error("Failed to load outage asset scope", error);
        if (!cancelled) setAssetScopeError("Couldn't load your outage asset list. Refresh the page or contact an admin.");
      }
    };
    fetchAssetScope();
    return () => { cancelled = true; };
  }, []);
  

  const convertToBostonTime = (utcDateTime: string | null) => {
    if (!utcDateTime) return null;

    return DateTime.fromISO(utcDateTime, { zone: "utc" }) // Parse as UTC
        .setZone("America/New_York") // Convert to Boston time
        .toFormat("yyyy-MM-dd'T'HH:mm:ss.SSS"); // Keep as datetime string without offset
  };

    const areArraysEqual = (a: Outage[], b: Outage[]) => {
      if (a.length !== b.length) return false;
      const mapA = new Map(a.map(o => [o.outage_id, o.sys_modified_date]));
      for (const o of b) {
        if (mapA.get(o.outage_id) !== o.sys_modified_date) return false;
      }
      return true;
    };
    
    const mergeOutages = (existing: Outage[], incoming: Outage[]) => {
      const outageMap = new Map<number, Outage>();
    
      // Fill with existing first
      existing.forEach(o => outageMap.set(o.outage_id, o));
    
      let isUpdated = false;
    
      // Merge incoming data
      incoming.forEach(newOutage => {
        const old = outageMap.get(newOutage.outage_id);
    
        if (!old || old.sys_modified_date !== newOutage.sys_modified_date) {
          outageMap.set(newOutage.outage_id, newOutage);
          isUpdated = true;
        }
      });
    
      // Final merged array
      const merged = Array.from(outageMap.values());
    
      return { merged, updated: isUpdated };
    };



  // const fetchOutages = async () => {
  //   try {
  //     const headers = await getOutageHeaders();
  //     const delay = (ms: number) => new Promise(res => setTimeout(res, ms));
  
  //     // Fetch Default mode first and display immediately
  //     const fetchModeDefault = async () => {
  //       let allOutages: any[] = [];
  //       let page = 1;
  //       const pageSize = 500;
  //       let hasMoreData = true;
  
  //       while (hasMoreData) {
  //         try {
  //           const response = await apiClientOutage.get(
  //             // `${process.env.REACT_APP_OUTAGE_API_URL}?page=${page}&page_size=${pageSize}&fetch_mode=Default`,
  //             `/gen-outage/?page=${page}&page_size=${pageSize}&fetch_mode=Default`,
  //             { headers }
  //           );
  
  //           if (response.data.response?.status === "success") {
  //             const convertedData = response.data.response.data.map((outage: any) => ({
  //               ...outage,
  //               start_datetime: convertToBostonTime(outage.start_datetime),
  //               end_datetime: convertToBostonTime(outage.end_datetime),
  //               sys_modified_date: convertToBostonTime(outage.sys_modified_date),
  //             }));
  
  //             allOutages = [...allOutages, ...convertedData];
  
  //             const totalRows = response.data.meta.total_rows;
  //             if (allOutages.length >= totalRows) {
  //               hasMoreData = false;
  //             } else {
  //               page++;
  //               await delay(500);
  //             }
  //           } else {
  //             handleApiError(null, "API returned unsuccessful status for Default mode");
  //             break;
  //           }
  //         } catch (error) {
  //           handleApiError(error, `Failed to fetch Default data (Page ${page})`);
  //           break;
  //         }
  //       }
  
  //       const { merged } = mergeOutages(outagesRef.current, allOutages);

  //       // Update only on FIRST ever load → prevents flicker
  //       if (firstLoadRef.current) {
  //           setOutages(merged);
  //       }
  
  //       return allOutages;
  //     };
  
  //     // Fetch All mode in the background
  //     const fetchModeAll = async (existingOutages: any[]) => {
  //       let allOutages: any[] = [];
  //       let page = 1;
  //       const pageSize = 5000;
  //       let hasMoreData = true;
  //       const maxPages = 5;
  
  //       while (hasMoreData && page <= maxPages) {
  //         try {
  //           const response = await apiClientOutage.get(
  //             // `${process.env.REACT_APP_OUTAGE_API_URL}?page=${page}&page_size=${pageSize}&fetch_mode=All`,
  //             `/gen-outage/?page=${page}&page_size=${pageSize}&fetch_mode=All`,
  //             { headers }
  //           );
  
  //           if (response.data.response?.status === "success") {
  //             const convertedData = response.data.response.data.map((outage: any) => ({
  //               ...outage,
  //               start_datetime: convertToBostonTime(outage.start_datetime),
  //               end_datetime: convertToBostonTime(outage.end_datetime),
  //               sys_modified_date: convertToBostonTime(outage.sys_modified_date),
  //             }));
  
  //             allOutages = [...allOutages, ...convertedData];
  
  //             const totalRows = response.data.meta.total_rows;
  //             if (allOutages.length >= totalRows) {
  //               hasMoreData = false;
  //             } else {
  //               page++;
  //               await delay(1000);
  //             }
  //           } else {
  //             handleApiError(null, "API returned unsuccessful status for All mode");
  //             break;
  //           }
  //         } catch (error) {
  //           handleApiError(error, `Failed to fetch All data (Page ${page})`);
  //           break;
  //         }
  //       }
  
  //       const { merged } = mergeOutages(existingOutages, allOutages);

  //       if (!areArraysEqual(outagesRef.current, merged)) {
  //           setOutages(merged);
  //       }

  //       // Mark that first load is done
  //       firstLoadRef.current = false;
  //     };
  
  //     const defaultOutages = await fetchModeDefault();
  //     fetchModeAll(defaultOutages);
  //   } catch (error) {
  //     handleApiError(error, "Error fetching outages");
  //     alert("Error fetching outages. Please try again later.");
  //   }
  // };

  const fetchOutages = useCallback(async () => {
    // 🚫 Prevent overlapping calls
    if (isFetchingRef.current) {
      return;
    }
  
    isFetchingRef.current = true;
  
    try {
      const headers = await getOutageHeaders();
      const delay = (ms: number) => new Promise(res => setTimeout(res, ms));
  
      const convertData = (data: any[]) =>
        data.map((outage: any) => ({
          ...outage,
          start_datetime: convertToBostonTime(outage.start_datetime),
          end_datetime: convertToBostonTime(outage.end_datetime),
          sys_modified_date: convertToBostonTime(outage.sys_modified_date),
        }));
  
      const fetchByMode = async (mode: "Default" | "Recent" | "All") => {
        let allOutages: any[] = [];
        let page = 1;
        const pageSize = mode === "All" ? 2000 : 500;
        let hasMoreData = true;
  
        while (hasMoreData) {
          const response = await apiClientOutage.get(
            `/gen-outage/?page=${page}&page_size=${pageSize}&fetch_mode=${mode}`,
            { headers }
          );
  
          if (response.data.response?.status === "success") {
            const converted = convertData(response.data.response.data);
            allOutages = [...allOutages, ...converted];
  
            const totalRows = response.data.meta.total_rows;
  
            if (allOutages.length >= totalRows) {
              hasMoreData = false;
            } else {
              page++;
              await delay(mode === "All" ? 1000 : 500);
            }
          } else {
            hasMoreData = false;
          }
        }
  
        return allOutages;
      };
  
      const secondaryMode = isHistoricalOn ? "All" : "Recent";
  
      const [defaultOutages, secondaryOutages] = await Promise.all([
        fetchByMode("Default"),
        fetchByMode(secondaryMode)
      ]);
  
      const { merged } = mergeOutages(defaultOutages, secondaryOutages);
  
      if (!areArraysEqual(outagesRef.current, merged)) {
        setOutages(merged);
        outagesRef.current = merged;
      }
  
    } catch (error) {
      handleApiError(error, "Error fetching outages");
    } finally {
      // ✅ Always release lock
      isFetchingRef.current = false;
    }
  
  }, [isHistoricalOn]);
  
  
  
  

  useEffect(() => {
    fetchOutages();
  }, [isHistoricalOn]);
  
  

//   useEffect(() => {
//     fetchOutages(); // Initial fetch when the component mounts

//     const interval = setInterval(() => {
//         fetchOutages(); // Fetch outages every 60 seconds
//     }, 60000);

//     return () => clearInterval(interval); // Cleanup on component unmount
// }, []); 
  // SmartPolling(fetchOutages, 60000, { inactivityTimeout: 120000 });
  // SmartPolling(
  //   fetchOutages,
  //   isHistoricalOn ? 120000 : 60000,  // 2 mins if ON, 1 min if OFF
  //   { inactivityTimeout: 300000 }
  // );
  SmartPolling(
    fetchOutages,
    60000,
    {
      inactivityTimeout: 300000,
      enabled: !isHistoricalOn // polling OFF in historical mode
    }
  );
  
  // console.log(outages);

  const handleRowSelect = (outage: any) => {
    if (isFormDirty || isCreateMode) {
      setDialog({
        open: true,
        title: "Unsaved Changes",
        message: "You have unsaved changes. Are you sure you want to discard them?",
        confirmButtonText: "Yes",
        cancelButtonText: "No",
        onConfirm: () => {
          setIsFormDirty(false);
          setIsCreateMode(false);
          setSelectedOutage(outage);
          setSelectedRow(outage.outage_name);
          closeDialog();
        },
        onCancel: closeDialog,
      });
    } else {
      setSelectedOutage(outage);
      setSelectedRow(outage.outage_name);
    }
  };
  // Clears the selection outright — callers that need a confirmation run their own
  // first (Clear/New shows the "create a new Outage?" dialog, Copy is deliberate).
  // Prompting here as well produced a second "Unsaved Changes" dialog straight after
  // the user had already confirmed: resetForm's onDirtyChange(false) is batched, so a
  // dirty check in this function still read the stale `true` from the same event.
  const onClearSelection = () => {
    setSelectedOutage(null);
    setSelectedRow(null);
    setIsFormDirty(false);
    setIsCreateMode(false);
  };

  // Resizer
  const [leftWidth, setLeftWidth] = useState(62); // Initial width of left container in percentage
  const isResizing = useRef(false);
  const startResizing = (event: React.MouseEvent) => {
    isResizing.current = true;
    document.addEventListener("mousemove", handleMouseMove);
    document.addEventListener("mouseup", stopResizing);

    // Disable text selection & change cursor
    document.body.style.userSelect = "none";
    document.body.style.cursor = "ew-resize";
  };

  const handleMouseMove = (event: MouseEvent) => {
    if (!isResizing.current) return;
    
    const container = document.getElementById("container");
    if (!container) return;
    
    const containerRect = container.getBoundingClientRect();
    const newLeftWidth = ((event.clientX - containerRect.left) / containerRect.width) * 100;
    
    if (newLeftWidth > 20 && newLeftWidth < 80) { // Restrict resizing between 20% and 80%
      setLeftWidth(newLeftWidth);
    }
  };

  const stopResizing = () => {
    isResizing.current = false;
    document.removeEventListener("mousemove", handleMouseMove);
    document.removeEventListener("mouseup", stopResizing);

    // Re-enable text selection & reset cursor
    document.body.style.userSelect = "";
    document.body.style.cursor = "";
  };

  

const convertBostonToUtc = (bostonTime: string) => {
  // Interpret bostonTime as a local Boston time (America/New_York)
  const bostonDateTime = DateTime.fromFormat(bostonTime, "yyyy-MM-dd'T'HH:mm:ss.SSS", { zone: "America/New_York" });
  // Convert it to UTC and return the string with 'Z'
  return bostonDateTime.toUTC().toFormat("yyyy-MM-dd'T'HH:mm:ss.SSS'Z'");
};


const handleCreateOutage = async (newOutage: any) => {
  datadogRum.addAction(
    `Creating Outage: ${newOutage.outage_name}`
  );
  const headers = await getOutageHeaders();
  try {
      let newOutageData = { ...newOutage };
      // console.log("Start:", newOutage.start_datetime, typeof newOutage.start_datetime);
      // console.log("End:", newOutage.end_datetime, typeof newOutage.end_datetime);
      const startStr = DateTime.fromJSDate(newOutage.start_datetime).toFormat("yyyy-MM-dd'T'HH:mm:ss.SSS");
      const endStr = DateTime.fromJSDate(newOutage.end_datetime).toFormat("yyyy-MM-dd'T'HH:mm:ss.SSS");
      newOutageData.start_datetime = convertBostonToUtc(startStr);
      newOutageData.end_datetime = convertBostonToUtc(endStr);
      newOutageData.sys_created_by = user ? `${user.email || user.fullName}` : "Unknown User";; // Set created by
      newOutageData.sys_modified_by = user ? `${user.email || user.fullName}` : "Unknown User";; // Set modified by
      
    // console.log("New data received:", newOutageData); // ✅ This will log the new form state

    const response = await apiClientOutage.post("/gen-outage/", newOutageData, {
      headers,
    });

    if (response.status === 201 || response.status === 200) {
      await fetchOutages(); // Wait for GET to finish after POST succeeds
      setDialog({
        open: true,
        title: "Success",
        message: `Outage successfully created.`,
        confirmButtonText: "Close",
        onConfirm: closeDialog,
      });
    } else {
      console.error("Unexpected response:", response);
    }
  } catch (err) {
    if (!handleConflictError(err, "Failed to Create Outage")) {
      handleApiError(err, "Failed to Create Outage");
    }
  }
};


const parseDate = (value: string | Date) => {
  return typeof value === "string"
    ? DateTime.fromISO(value)
    : DateTime.fromJSDate(value);
};


const handleSaveOutage = async (updatedOutage: any) => {
  datadogRum.addAction(`Updating Outage: ${updatedOutage.outage_name}`);
  const headers = await getOutageHeaders();

  let updatedOutageData = { ...updatedOutage };

  // Handle both string and Date inputs
  const startDateTime = parseDate(updatedOutage.start_datetime);
  const endDateTime = parseDate(updatedOutage.end_datetime);

  if (!startDateTime.isValid || !endDateTime.isValid) {
    console.error("❌ Invalid date values:", updatedOutage.start_datetime, updatedOutage.end_datetime);
    return;
  }

  const startStr = startDateTime.toFormat("yyyy-MM-dd'T'HH:mm:ss.SSS");
  const endStr = endDateTime.toFormat("yyyy-MM-dd'T'HH:mm:ss.SSS");

  updatedOutageData.start_datetime = convertBostonToUtc(startStr);
  updatedOutageData.end_datetime = convertBostonToUtc(endStr);

  updatedOutageData.sys_modified_by = user ? `${user.email || user.fullName}` : "Unknown User";
  updatedOutageData.sys_created_by = selectedOutage.sys_created_by || "Unknown User";

  const outage_id = selectedOutage?.outage_id;
  if (!outage_id || isNaN(Number(outage_id))) {
    console.error("❌ Missing or invalid outage_id!", outage_id);
    return;
  }

  // console.log("Final payload being sent:", { outage_id, body: updatedOutageData });

  try {
    const response = await apiClientOutage.put(`/gen-outage/${outage_id}`, updatedOutageData, { headers });

    if (response.status === 200) {
      setOutages((prev) =>
        prev.map((outage) =>
          outage.outage_id === outage_id ? { ...outage, ...updatedOutageData } : outage
        )
      );
    } else {
      console.error("Unexpected response:", response);
    }

    setDialog({
      open: true,
      title: "Success",
      message: `Outage "${updatedOutage.outage_name}" successfully updated.`,
      confirmButtonText: "Close",
      onConfirm: () => closeDialog(),
    });
  } catch (err: any) {
    if (!handleConflictError(err, "Failed to Update Outage")) {
      handleApiError(err, "Failed to Update Outage");
    }
  }
};




    return (

    // <div className="flex flex-col xl:flex-row xl:items-stretch bg-gray-100 p-4 min-h-screen gap-4">
    // h-full, not h-screen: the parent card already owns a bounded slice of the
    // viewport, so claiming a second full 100vh here is what made the page
    // taller than the window and produced the outer scrollbar.
    <div ref={containerRef} className="flex flex-col xl:flex-row bg-gray-100 p-4 short:p-2 h-full min-h-0 overflow-hidden">

      {dialog.open && (
        <Dialog
          title={dialog.title}
          message={dialog.message}
          confirmButtonText={dialog.confirmButtonText}
          cancelButtonText={dialog.cancelButtonText}
          onConfirm={dialog.onConfirm}
          onCancel={dialog.onCancel}
          onClose={closeDialog}
        />
      )}


        {/* <div className="flex-grow flex flex-col lg:w-2/3 bg-white rounded-xl shadow-xl overflow-visible relative"> */}
        <div className="flex flex-col bg-white rounded-xl shadow-xl relative overflow-hidden h-full" style={{ width: `${tableWidthPct}%` }}>

          {loading && (
            <div className="absolute inset-0 flex items-center justify-center bg-white bg-opacity-75 z-30">
              <LoadingStyle />
            </div>
          )}
          <OutageTable
            assets={assets}
            outages={outages}
            onEditOutage={handleSaveOutage}
            onRowSelect={handleRowSelect}
            onClearSelection={onClearSelection}
            selectedRow={selectedRow}  
            // onDeleteSelected={handleDeleteSelected}
            selectedOutage={selectedOutage}
            setSelectedOutage={setSelectedOutage}
            fetchAssets={fetchAssets}
            isHistoricalOn={isHistoricalOn}
            onToggleHistorical={setIsHistoricalOn}
            onRefresh={fetchOutages}
          />
        </div>

        {/* Draggable resizer */}
        <div
          className="hidden xl:flex items-center justify-center flex-shrink-0 w-3 cursor-col-resize group"
          onMouseDown={handleDividerMouseDown}
        >
          <div className="w-1 h-full rounded-full bg-gray-300 group-hover:bg-blue-400 transition-colors duration-150" />
        </div>

        {/* <div className="flex-grow flex flex-col lg:basis-1/3 xl:self-stretch bg-white"> */}
        <div className="flex flex-col bg-white rounded-xl shadow-xl overflow-hidden h-full" style={{ flex: 1, minWidth: 0 }}>

          <OutageForm
            onCreateOutage={handleCreateOutage}
            onSaveOutage={handleSaveOutage}
            selectedOutage={selectedOutage}
            assets={assets}
            assetScope={assetScope}
            assetScopeError={assetScopeError}
            onClearSelection={onClearSelection}
            onDirtyChange={setIsFormDirty}
            onModeChange={(mode) => setIsCreateMode(mode === "create")}
          />
        </div>

    </div>
    );
  };
  
  export default OutageSetup;
  